import { useContext } from "react";
import { Typography, Table, Button, Divider } from "antd";
import { useParams } from "react-router-dom";
import PosthogHelper from "../../../helpers/posthog.helper";
import classNames from "classnames";
import { UserContext } from "../../../contexts/user.context";
import FlowType from "../../../enums/flowType.enum";
import { useNavigate } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";
const { Paragraph } = Typography;

export const MercuryPost = () => {
  const { currentUser } = useContext(UserContext);
  const { blogId } = useParams();
  const navigate = useNavigate();
  return (
    <div className={classNames("blog-post", "mercury-post")}>
      <div className="blog-border" /><br/><br/>
      <div className="blog-body">
        <Paragraph className="blog-description">
         Mercury is a toxic heavy metal that can affect the brain, kidneys, and nervous system. Even small exposures—whether through fish, old thermometers, or industrial materials—can build up in the body over time. Testing helps identify whether mercury is playing a hidden role in symptoms like fatigue, brain fog, tremors, or mood changes.
        </Paragraph>
        <Paragraph className="blog-description">
          Because mercury exists in different forms—each handled differently by the body—testing must be tailored to your exposure history. That’s why it’s important to test the right biological sample (blood, urine, or hair) at the right time.
        </Paragraph>

        <Paragraph className="blog-title">At-a-Glance: Which Test Should You Get?</Paragraph>

        <Table
          className="blog-table"
          dataSource={[
            {
              exposure: 'Seafood (methylmercury)',
              test: <><a href={`/test/mercury`} target="_blank">Blood Test</a>, <a href={`/flow/${FlowType.TOXIC_METALS_TEST}`} target="_blank">Hair Test</a></>,
              why: 'Methylmercury builds up in blood and deposits in hair'
            },
            {
              exposure: 'Mercury vapor (thermometers, industry)',
              test: <><a href={`/test/mercury`} target="_blank">Blood Test</a>, <a href={`/flow/${FlowType.TOXIC_METALS_TEST}`} target="_blank">Urine Test</a></>,
              why: 'Vapor raises blood mercury first, then shifts to urine'
            },
            {
              exposure: 'Inorganic mercury (rare, e.g. old creams)',
              test: <><a href={`/test/mercury`} target="_blank">Urine Test</a></>,
              why: 'Best reflects ongoing excretion of inorganic mercury via the kidneys'
            },
            {
              exposure: 'Not sure / mixed exposure',
              test: <><a href={`/test/mercury`} target="_blank">Blood Test</a>, <a href={`/flow/${FlowType.TOXIC_METALS_TEST}`} target="_blank">Urine Test</a></>,
              why: 'Captures both recent and chronic exposures across multiple forms'
            }
          ]}
          pagination={false}
          size='small'
          bordered
        >
          <Table.Column title="If you were exposed to..." dataIndex="exposure" />
          <Table.Column title="Best Type of Test" dataIndex="test" />
          <Table.Column title="Why" dataIndex="why" />
        </Table>

        <Paragraph className="blog-title">Why Mercury Is Harmful</Paragraph>
        <Paragraph className="blog-description">
          Mercury causes damage through oxidative stress and direct toxicity to cells. Once in the body, it targets the brain, nerves, and kidneys. Symptoms can be vague at first—like anxiety, poor memory, or numbness—but worsen over time if the exposure continues.
        </Paragraph>
        <Paragraph className="blog-description">
          Early detection lets you act—by identifying the source, removing the exposure, and starting chelation or other detox strategies if needed.
        </Paragraph>

        <Paragraph className="blog-title">Blood Testing: Best for Recent or Dietary Exposure</Paragraph>
        <Paragraph className="blog-cta">
            <Button 
                type="primary" 
                size="large"
                iconPosition="end"
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(`/test/mercury`)}>Order the Blood Mercury Test</Button>
        </Paragraph>

        <Paragraph className="blog-description">
          Blood testing is the most direct way to measure recent exposure, especially to methylmercury (the kind from fish) or elemental mercury vapor (inhaled from spills or industrial sources). Mercury appears in blood soon after exposure, either inside red blood cells or in plasma.
        </Paragraph>
        <Paragraph className="blog-description">
          <ul>
            <li>
              <strong>Methylmercury</strong> stays in the blood for weeks to months. This makes blood the best test if you eat fish regularly or suspect dietary exposure.
            </li>
            <li>
              <strong>Elemental mercury</strong> (from vapor) causes a brief spike in blood levels that fades within a few days. So timing is critical—blood must be drawn quickly after exposure.
            </li>
            <li>
              <strong>Inorganic mercury</strong> may appear briefly in blood but is better detected in urine due to how it’s processed.
            </li>
          </ul>
        </Paragraph>

        <Paragraph className="blog-description">
          <strong>When is blood testing useful?</strong> Right after a known exposure or to evaluate ongoing fish-related mercury intake. It’s also the only test that directly reflects methylmercury levels.
        </Paragraph>


        <Paragraph className="blog-title">Urine Testing: Best for Chronic or Occupational Exposure</Paragraph>
        <Paragraph className="blog-cta">
            <Button 
                type="primary" 
                size="large"
                iconPosition="end"
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(`/flow/${FlowType.TOXIC_METALS_TEST}`)}>Order the Urine Mercury Test</Button>
        </Paragraph>

        <Paragraph className="blog-description">
          Urine is the best sample for elemental or inorganic mercury, especially if exposure is chronic. After entering the body, these forms of mercury accumulate in organs and are slowly excreted by the kidneys.
        </Paragraph>
        <Paragraph className="blog-description">
            <ul>    
                <li>
                    <strong>Elemental mercury</strong> (from vapor) causes a brief spike in blood levels that fades within a few days. So timing is critical—blood must be drawn quickly after exposure.
                </li>
                <li>
                    <strong>Inorganic mercury</strong> may appear briefly in blood but is better detected in urine due to how it’s processed.
                </li>
                <li>
                    <strong>Methylmercury</strong> doesn’t show up in urine, since it’s mostly eliminated in feces.
                </li>   
            </ul>
        </Paragraph>
        <Paragraph className="blog-description">
          <strong>When is urine testing useful?</strong> For evaluating long-term or occupational exposure, and for monitoring recovery after an incident. It’s less helpful in the first 24-48 hours after an acute exposure, since mercury takes time to pass into urine.
        </Paragraph>    

        <Paragraph className="blog-title">Hair Analysis: Best for Long-Term Dietary Exposure</Paragraph>
        <Paragraph className="blog-cta">
            <Button 
                type="primary" 
                size="large"
                iconPosition="end"
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(`/flow/${FlowType.TOXIC_METALS_TEST}`)}>Order the Hair Mercury Test</Button>
        </Paragraph>
        <Paragraph className="blog-description">    
          Hair testing is uniquely helpful for long-term methylmercury exposure, especially from diet. Mercury binds to keratin as hair grows-about 1 cm per month-so a strand of hair becomes a timeline of past exposure.
        </Paragraph>
        <Paragraph className="blog-description">
          For example, the first 6 cm of scalp hair can reflect the last six months of dietary mercury intake. This is especially helpful if someone has recently stopped eating fish; even if blood levels drop, hair still holds the history.
        </Paragraph>
        <Paragraph className="blog-description">
          Hair is not reliable for detecting elemental or inorganic mercury, as those forms don’t integrate into hair tissue.
        </Paragraph>
        <Paragraph className="blog-description">
          <strong>When is hair testing useful?</strong> When you want to understand cumulative exposure from diet, especially over several months. It’s commonly used in research and public health monitoring.
        </Paragraph>

        <Paragraph className="blog-title">Not Sure Which Test You Need?</Paragraph>
        <Paragraph className="blog-description">
          No problem—many people benefit from doing both a <a href={`/test/mercury`} target="_blank">blood test</a> and a <a href={`/flow/${FlowType.TOXIC_METALS_TEST}`} target="_blank">urine test</a> to get a full picture. If you have neurological symptoms, known exposure, or a high-fish diet, we recommend starting with at least one test to get data on your body’s burden.
        </Paragraph>
        <Paragraph className="blog-title">Final Thoughts</Paragraph>
        <Paragraph className="blog-description">
        Mercury toxicity is more common than most people think—and it’s often missed. Whether you’re eating a lot of seafood, working with metals, or simply feeling off without a clear cause, testing is the first step toward answers. The best mercury test depends on what you were exposed to and when:
        </Paragraph>
        <Paragraph className="blog-description">
            <ul>
                <li>
                    <strong>Blood</strong> is best for recent exposure, especially from fish or vapor.
                </li>
                <li>
                    <strong>Urine</strong> is best for chronic exposure to elemental or inorganic mercury.
                </li>
                <li>
                    <strong>Hair</strong> is best for understanding long-term dietary exposure to methylmercury.
                </li>
            </ul>
        </Paragraph>
        <Paragraph className="blog-description">
          If you’ve had a potential exposure but not sure what kind—or have unexplained symptoms like tremors, fatigue, or memory changes, a combination of these tests is needed to get the full picture.
        </Paragraph>


        <div className="sources">
          <Paragraph className="blog-title">Sources</Paragraph>
          <ul className="source-list">
            <li>
              Mercury (Fisher & Gupta, StatPearls, 2024) <a onClick={() => window.open('https://www.ncbi.nlm.nih.gov/books/NBK557806/', '_blank')}>ncbi.nlm.nih.gov</a>
            </li>
            <li>
              The toxicology of mercury—current exposures and clinical manifestations (Clarkson et al., New England Journal of Medicine, 2003) <a onClick={() => window.open('https://doi.org/10.1056/NEJMra022471', '_blank')}>doi.org</a>
            </li>
            <li>
                Mercury exposure and health impacts in dental personnel (Aaseth et al., Environ Res, 2018) <a onClick={() => window.open('https://pubmed.ncbi.nlm.nih.gov/29482185/', '_blank')}>pubmed.ncbi.nlm.nih.gov</a>
            </li>

          </ul>
        </div>
      </div>
    </div>
  );
};
